import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import Menu from "lucide-react/dist/esm/icons/menu.js";
import X from "lucide-react/dist/esm/icons/x.js";
import Globe from "lucide-react/dist/esm/icons/globe.js";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down.js";
import Check from "lucide-react/dist/esm/icons/check.js";
import { useQuery } from "@tanstack/react-query";
import { useI18n, localized, pick, LOCALE_LABELS } from "@/lib/i18n";
import { useLp, useChangeLocale } from "@/lib/localePath";
import { usePageLocales } from "@/lib/pageLocales";
import { COPY } from "@/lib/uiCopy";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fetchServices } from "@/lib/queries";
import { useDeferredReady } from "@/hooks/use-deferred-ready";
import { fallbackServices, mergeServiceFallbacks, servicePath } from "@/lib/serviceData";
import logoUrl from "@/assets/logo.webp";

export function Header() {
  const { t, locale } = useI18n();
  const { user, isAdmin } = useAuth();
  const lp = useLp();
  const changeLocale = useChangeLocale();
  // Blog yazısı gibi sayfalarda sadece gerçek çevirisi olan diller listelenir
  // (bkz. src/lib/pageLocales.tsx) — aksi halde çevirisi olmayan bir dile
  // tıklamak "yazı bulunamadı" sayfasına kırık yönlendirme yapardı.
  const availableLocales = usePageLocales();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const ready = useDeferredReady(1500);
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: fetchServices, enabled: ready });
  const serviceItems = mergeServiceFallbacks(services && services.length > 0 ? services : fallbackServices);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/felsefemiz", label: t("nav.philosophy") },
    { to: "/hizmetlerimiz", label: t("nav.services") },
    { to: "/hizmet-alanlarimiz", label: t("nav.serviceAreas") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/insan-kaynaklari", label: t("nav.hr") },
    { to: "/iletisim", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/85 backdrop-blur-lg">
      <div className="container-page flex h-20 items-center justify-between gap-6">
        <Link
          to={lp("/")}
          className="flex items-center group"
          aria-label="Anadolu Terra"
          onClick={(e) => {
            // Zaten ana sayfadaysak: tam sıfırlama (scroll + state için reload)
            const homePath = lp("/");
            if (window.location.pathname === homePath || window.location.pathname === homePath.replace(/\/$/, "")) {
              e.preventDefault();
              window.scrollTo({ top: 0, left: 0, behavior: "auto" });
              // Açık menü/akordeon vb. tüm geçici durumları sıfırlamak için sayfayı tazele
              window.location.reload();
            } else {
              window.scrollTo({ top: 0, left: 0, behavior: "auto" });
            }
          }}
        >
          <img
            src={logoUrl}
            alt="Anadolu Terra"
            width={140}
            height={56}
            decoding="async"
            className="h-12 md:h-14 w-auto object-contain transition-base group-hover:scale-[1.02]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => {
            if (l.to === "/hizmetlerimiz") {
              return (
                <div
                  key={l.to}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <NavLink
                    to={lp(l.to)}
                    className={({ isActive }) =>
                      cn(
                        "inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-sm font-medium transition-base hover:bg-accent hover:text-accent-foreground",
                        isActive ? "text-accent" : "text-foreground/80",
                        servicesOpen && "bg-accent text-accent-foreground"
                      )
                    }
                  >
                    {l.label}
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", servicesOpen && "rotate-180")} />
                  </NavLink>
                  {servicesOpen && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50 animate-fade-in">
                      <div className="min-w-[260px] rounded-xl border border-border bg-background shadow-elevated p-2">
                        {serviceItems.map((s: any) => (
                          <Link
                            key={s.id}
                            to={lp(servicePath(s.slug))}
                            className="block rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/85 hover:bg-accent hover:text-accent-foreground transition-base"
                            onClick={() => setServicesOpen(false)}
                          >
                            {localized(s, "title", locale)}
                          </Link>
                        ))}
                        <div className="mt-1 border-t border-border pt-1">
                          <Link
                            to={lp("/hizmetlerimiz")}
                            className="block rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wider text-accent hover:bg-accent hover:text-accent-foreground transition-base"
                            onClick={() => setServicesOpen(false)}
                          >
                            {pick(locale, COPY.viewAllServices)} →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={lp(l.to)}
                end={l.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "rounded-full px-3.5 py-1.5 text-sm font-medium transition-base hover:bg-accent hover:text-accent-foreground",
                    isActive ? "text-accent" : "text-foreground/80"
                  )
                }
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <div
            className="relative hidden sm:block"
            onMouseEnter={() => setLangOpen(true)}
            onMouseLeave={() => setLangOpen(false)}
          >
            <button
              className={cn(
                "flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold uppercase text-foreground/80 transition-base hover:border-accent hover:text-accent",
                langOpen && "border-accent text-accent"
              )}
              aria-haspopup="menu"
              aria-expanded={langOpen}
            >
              <Globe className="h-3.5 w-3.5" />
              {locale.toUpperCase()}
              <ChevronDown className={cn("h-3 w-3 transition-transform", langOpen && "rotate-180")} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full pt-2 z-50 animate-fade-in">
                <div className="min-w-[180px] rounded-xl border border-border bg-background shadow-elevated p-1.5">
                  {availableLocales.map((l) => {
                    const info = LOCALE_LABELS[l];
                    const active = l === locale;
                    return (
                      <button
                        key={l}
                        onClick={() => {
                          changeLocale(l);
                          setLangOpen(false);
                        }}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-base",
                          active
                            ? "bg-accent text-accent-foreground"
                            : "text-foreground/85 hover:bg-accent hover:text-accent-foreground"
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-base leading-none">{info.flag}</span>
                          <span>{info.native}</span>
                        </span>
                        {active && <Check className="h-3.5 w-3.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Mobile: language dropdown next to hamburger so it's always reachable */}
          <div className="relative sm:hidden">
            <button
              onClick={() => setMobileLangOpen((v) => !v)}
              className={cn(
                "flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-semibold uppercase text-foreground/80 transition-base",
                mobileLangOpen && "border-accent text-accent"
              )}
              aria-haspopup="menu"
              aria-expanded={mobileLangOpen}
              aria-label={locale.toUpperCase()}
            >
              <Globe className="h-3.5 w-3.5" />
              {locale.toUpperCase()}
              <ChevronDown className={cn("h-3 w-3 transition-transform", mobileLangOpen && "rotate-180")} />
            </button>
            {mobileLangOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 animate-fade-in">
                <div className="min-w-[180px] rounded-xl border border-border bg-background shadow-elevated p-1.5">
                  {availableLocales.map((l) => {
                    const info = LOCALE_LABELS[l];
                    const active = l === locale;
                    return (
                      <button
                        key={l}
                        onClick={() => {
                          changeLocale(l);
                          setMobileLangOpen(false);
                        }}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-base",
                          active
                            ? "bg-accent text-accent-foreground"
                            : "text-foreground/85 hover:bg-accent hover:text-accent-foreground"
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-base leading-none">{info.flag}</span>
                          <span>{info.native}</span>
                        </span>
                        {active && <Check className="h-3.5 w-3.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <Button asChild variant="default" size="sm" className="hidden md:inline-flex bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent">
            <Link to={lp("/teklif-iste")}>{t("nav.quote")}</Link>
          </Button>
          {user && isAdmin && (
            <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
              <Link to={lp("/admin")}>{t("nav.admin")}</Link>
            </Button>
          )}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden rounded-md p-2 text-foreground hover:bg-secondary"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background animate-fade-in">
          <nav className="container-page flex flex-col gap-1 py-4">
            {links.map((l) => (
              <div key={l.to}>
                <NavLink
                  to={lp(l.to)}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "block rounded-md px-3 py-2.5 text-sm font-medium transition-base",
                      isActive ? "bg-accent text-accent-foreground" : "text-foreground/80 hover:bg-accent hover:text-accent-foreground"
                    )
                  }
                >
                  {l.label}
                </NavLink>
                {l.to === "/hizmetlerimiz" && (
                  <div className="ml-3 mt-1 mb-1 border-l border-border pl-3 space-y-0.5">
                    {serviceItems.map((s: any) => (
                      <Link
                        key={s.id}
                        to={lp(servicePath(s.slug))}
                        onClick={() => setOpen(false)}
                        className="block rounded-md px-3 py-2 text-sm text-foreground/70 hover:bg-accent hover:text-accent-foreground transition-base"
                      >
                        {localized(s, "title", locale)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center gap-2 pt-3">
              <Button asChild className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setOpen(false)}>
                <Link to={lp("/teklif-iste")}>{t("nav.quote")}</Link>
              </Button>
            </div>
            {user && isAdmin && (
              <Button asChild variant="outline" className="mt-2" onClick={() => setOpen(false)}>
                <Link to={lp("/admin")}>{t("nav.admin")}</Link>
              </Button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
